import {
  Badge,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  EquipUITheme,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import {
  NOTIFICATION_HEADER_STYLES,
  NotificationEventType,
  NotificationStatus,
} from '~/lib/constants/notifications.constants';
import { getNotificationText } from '~/lib/util/notification.util';

import NotificationAvatar from '../NotificationAvatar';
import NotificationTimeStamp from '../NotificationTimeStamp';

const TOP_LEVEL_ID = 'notification-inquiry';

interface NotificationInquiryProps {
  createdAt?: string;
  data?: any;
  eventType?: NotificationEventType;
  isRead?: boolean;
  notificationExternalId?: string;
  onUpdateNotification?: (
    notificationExternalIds: Array<string>,
    status: NotificationStatus,
  ) => void;
  patientName?: string;
}

const { label, labelBold, paragraph, paragraphBold } = EquipUITheme.typography;

const NotificationInquiry = ({
  createdAt,
  data,
  eventType,
  isRead,
  notificationExternalId,
  onUpdateNotification,
  patientName,
}: NotificationInquiryProps) => {
  const id = `${TOP_LEVEL_ID}__${notificationExternalId}`;
  // TODO: Normalize notification bodies that show Inquiry related data. This component assumes the notification is
  //  ASSIGN_INQUIRY without specifying how.
  let secondaryText = '';
  if (data?.patientDateOfBirth || data?.createdAt) {
    const dateOfBirth = data?.patientDateOfBirth
      ? DateTime.fromSQL(data?.patientDateOfBirth).toFormat('LL/dd/yyyy')
      : null;
    const inquiryCreatedDate = DateTime.fromJSDate(
      new Date(data?.createdAt),
    ).toFormat('MMM d, yyyy');
    secondaryText = dateOfBirth
      ? `${dateOfBirth} - Created ${inquiryCreatedDate}`
      : `Created ${inquiryCreatedDate}`;
  }
  return (
    <>
      <ChakraFlex
        alignItems="start"
        id={`${id}__notification-header`}
        justifyContent="space-between"
      >
        <ChakraBox
          {...paragraph}
          id={`${TOP_LEVEL_ID}-header`}
          {...NOTIFICATION_HEADER_STYLES}
        >
          <span>
            {getNotificationText(eventType)}
            {/* TODO: To avoid this special case for ASSIGNED_INQUIRY, consider having getNotificationText return a
                  React component instead that supports formatting. */}
            {eventType === NotificationEventType.ASSIGNED_INQUIRY && (
              <span style={{ ...paragraphBold, lineHeight: '16.8px' }}>
                &nbsp;Admissions Team
              </span>
            )}
            &nbsp;for the inquiry:
          </span>
        </ChakraBox>
        <NotificationTimeStamp
          createdAt={createdAt}
          isRead={isRead}
          notificationExternalId={notificationExternalId}
          onUpdateNotification={onUpdateNotification}
        />
      </ChakraFlex>
      <ChakraFlex
        backgroundColor="neutral.background.secondary"
        borderRadius="8px"
        id={`${TOP_LEVEL_ID}__wrapper`}
        justifyContent="space-between"
        marginTop="8px"
        padding="8px"
      >
        <ChakraFlex>
          <NotificationAvatar patientName={patientName} />
          <ChakraFlex direction="column" paddingLeft="18px">
            <ChakraText id={`${TOP_LEVEL_ID}__name`} {...labelBold}>
              {patientName}
            </ChakraText>
            <ChakraText id={`${TOP_LEVEL_ID}__dob`} {...label}>
              {secondaryText}
            </ChakraText>
          </ChakraFlex>
        </ChakraFlex>
        <Badge
          badgeColor="neutral.line"
          fontColor="neutral.secondary"
          fontSize="10px"
          fontWeight={700}
          id={`${TOP_LEVEL_ID}__channel`}
          margin="0"
        >
          {data?.channel}
        </Badge>
      </ChakraFlex>
    </>
  );
};

export default NotificationInquiry;
